import { useEffect, useRef } from "react";

// --- tunable constants (reference design) ---
const ACCENT_COLOR = "#8E2118"; // outbound lane
const RETURN_COLOR = "#28405E"; // return lane
const IDLE_SPIN = 6.5; // deg/s
const WIREFRAME_DENSITY = 15;
const ARC_ALTITUDE = 0.24;
const SATELLITES_PER_HUB = 3;
const SATELLITE_SPREAD = 3.5;
const SHOW_SATELLITES = true;

const D = Math.PI / 180;

const CITY = [
  { n: "Tokyo", jp: "東京", lat: 35.68, lon: 139.77, hub: "jp" },
  { n: "Osaka", jp: "大阪", lat: 34.69, lon: 135.5, hub: "osa" },
  { n: "Toronto", jp: "トロント", lat: 43.65, lon: -79.38, hub: "ca" },
  { n: "Sendai", lat: 38.26, lon: 140.87, of: "jp", li: 0, order: 0, side: -1, sp: 0.29 },
  { n: "Tōhoku", lat: 39.7, lon: 141.15, of: "jp", li: 1, order: 1, side: 1, sp: 0.29 },
  { n: "Nagoya", lat: 35.18, lon: 136.91, of: "jp", li: 2, order: 2, side: 1, sp: 0.29 },
  { n: "Hyōgo", lat: 34.69, lon: 135.19, of: "osa", li: 0, order: 3, side: -1, sp: 0.71 },
  { n: "Ottawa", lat: 45.42, lon: -75.7, of: "ca", li: 0, order: 4, side: -1 },
  { n: "London", lat: 42.98, lon: -81.25, of: "ca", li: 1, order: 5, side: 1 },
  { n: "Kingston", lat: 44.23, lon: -76.49, of: "ca", li: 2, order: 6, side: 1 },
  { n: "Stratford", lat: 43.37, lon: -80.98, of: "ca", li: 3, order: 7, side: -1 },
];

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const inv = (v, a, b) => clamp((v - a) / (b - a), 0, 1);
const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const vec = (lat, lon) => {
  const cl = Math.cos(lat * D);
  return [cl * Math.cos(lon * D), Math.sin(lat * D), cl * Math.sin(lon * D)];
};

const CAPTIONS = [
  { eyebrow: "01 — Two markets", en: "One practice, held at both ends.", sub: "二つの市場、一つの架け橋。", jp: true },
  { eyebrow: "02 — The corridor", en: "10,340 km, flown weekly.", sub: "羽田 — トロント・ピアソン、直行。", jp: true },
  { eyebrow: "03 — Beyond the hubs", en: "Where the work actually lands.", sub: "Sendai · Tōhoku · Nagoya · Hyōgo — Ottawa · London · Kingston.", jp: false },
  { eyebrow: "04 — In both directions", en: "Someone is always in the air.", sub: "往きも、還りも。", jp: true },
];

export const GlobeHero = () => {
  const sectionRef = useRef(null);
  const canvasRef = useRef(null);
  const heroRef = useRef(null);
  const cueRef = useRef(null);
  const capRefs = useRef([]);

  useEffect(() => {
    const cv = canvasRef.current;
    const scroller = sectionRef.current;
    const hero = heroRef.current;
    const cue = cueRef.current;
    const caps = capRefs.current;
    const ctx = cv.getContext("2d");

    let w = 0;
    let h = 0;
    const resize = () => {
      const r = cv.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      w = r.width;
      h = r.height;
      cv.width = Math.round(w * dpr);
      cv.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const TARGET_LAT = 57;
    const TARGET_LON = -159;
    const v0 = vec(35.68, 139.77);
    const v1 = vec(43.65, -79.38);
    const dot = v0[0] * v1[0] + v0[1] * v1[1] + v0[2] * v1[2];
    const om = Math.acos(clamp(dot, -1, 1));
    const sinOm = Math.sin(om);

    let nx = v0[1] * v1[2] - v0[2] * v1[1];
    let ny = v0[2] * v1[0] - v0[0] * v1[2];
    let nz = v0[0] * v1[1] - v0[1] * v1[0];
    const nl = Math.hypot(nx, ny, nz);
    nx /= nl; ny /= nl; nz /= nl;
    const slerp = (t) => {
      const s0 = Math.sin((1 - t) * om) / sinOm;
      const s1 = Math.sin(t * om) / sinOm;
      return [v0[0] * s0 + v1[0] * s1, v0[1] * s0 + v1[1] * s1, v0[2] * s0 + v1[2] * s1];
    };
    const gcMix = (a, b, t) => {
      const d = clamp(a[0] * b[0] + a[1] * b[1] + a[2] * b[2], -1, 1);
      const o = Math.acos(d);
      if (o < 1e-6) return a.slice();
      const s = Math.sin(o);
      const k0 = Math.sin((1 - t) * o) / s;
      const k1 = Math.sin(t * o) / s;
      return [a[0] * k0 + b[0] * k1, a[1] * k0 + b[1] * k1, a[2] * k0 + b[2] * k1];
    };
    const geoScale = (a, b, k) => {
      const d = clamp(a[0] * b[0] + a[1] * b[1] + a[2] * b[2], -1, 1);
      const o = Math.acos(d);
      if (o < 1e-6) return a.slice();
      let tx = b[0] - a[0] * d, ty = b[1] - a[1] * d, tz = b[2] - a[2] * d;
      const tl = Math.hypot(tx, ty, tz);
      tx /= tl; ty /= tl; tz /= tl;
      const ang = o * k;
      const ca = Math.cos(ang), sa = Math.sin(ang);
      return [a[0] * ca + tx * sa, a[1] * ca + ty * sa, a[2] * ca + tz * sa];
    };
    const vOsa = vec(34.69, 135.5);
    const nrmOf = (a, b) => {
      const x = a[1] * b[2] - a[2] * b[1], y = a[2] * b[0] - a[0] * b[2], z = a[0] * b[1] - a[1] * b[0];
      const l = Math.hypot(x, y, z) || 1;
      return [x / l, y / l, z / l];
    };
    const drift = [];
    for (let i = 0; i < 84; i++) {
      drift.push({ t: Math.random(), off: (Math.random() * 2 - 1) * 0.34, v: 0.006 + Math.random() * 0.014, r: 0.8 + Math.random() * 1.4, a: 0.4 + Math.random() * 0.6 });
    }

    let spin = 150;
    let last = performance.now();
    let raf = 0;

    const frame = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const r = scroller.getBoundingClientRect();
      const total = Math.max(1, r.height - window.innerHeight);
      const p = clamp(-r.top / total, 0, 1);

      const zoomP = ease(inv(p, 0.12, 0.46));
      const arcP = ease(inv(p, 0.46, 0.7));
      const satP = ease(inv(p, 0.66, 0.86));
      const s1 = ease(inv(p, 0.44, 0.58));
      const s2 = ease(inv(p, 0.64, 0.78));
      const s3 = ease(inv(p, 0.84, 0.98));
      const heroFade = 1 - inv(p, 0.02, 0.15);

      spin += dt * IDLE_SPIN * (1 - zoomP * 0.92);
      let delta = ((TARGET_LON * -1 + 90) - spin) % 360;
      if (delta > 180) delta -= 360;
      if (delta < -180) delta += 360;
      const rotY = (spin + delta * zoomP) * D;
      const tilt = (14 + (TARGET_LAT - 14) * zoomP - 5 * s2 - 26 * s3) * D;
      const min = Math.min(w, h);
      const cx = w / 2;
      const sr = Math.sin(rotY), cr = Math.cos(rotY), st = Math.sin(tilt), ct = Math.cos(tilt);

      const accent = ACCENT_COLOR;
      const step = WIREFRAME_DENSITY;
      const alt = ARC_ALTITUDE * (1 + 0.35 * s2 + 1.7 * s3);
      const phone = w < 640;
      const small = w < 1024;
      // fewer satellites per hub on narrow screens so labels never crowd the frame
      const perHub = phone ? 1 : small ? 2 : SATELLITES_PER_HUB;
      const spread = SATELLITE_SPREAD;
      const HUBV = { jp: v0, ca: v1, osa: vOsa };
      const satVec = (c) => geoScale(HUBV[c.of], vec(c.lat, c.lon), spread * (c.sp || 1));

      const dirOf = (v, a) => {
        const k = 1 + (a || 0);
        const x = v[0] * k, y = v[1] * k, z = v[2] * k;
        const x2 = x * cr - z * sr;
        const z1 = z * cr + x * sr;
        return [x2, y * ct - z1 * st, y * st + z1 * ct];
      };

      // camera: continuous zoom, then three eased downward steps. X locked to centre.
      let R = min * (0.3 + 0.34 * zoomP) * (1 + 0.1 * s1 - 0.05 * s2 + 0.56 * s3);
      let cy = h / 2 + h * (0.05 * zoomP + 0.1 * s1 + 0.16 * s2 + 0.5 * s3);

      // GUARD: keep hubs + satellites inside the frame at every step
      const ground = [];
      CITY.forEach((c) => {
        if (c.hub) ground.push(dirOf(HUBV[c.hub], 0));
        else if (c.li < perHub) ground.push(dirOf(satVec(c), 0));
      });
      let umax = 0.05, gvmin = 9, gvmax = -9, any = false;
      ground.forEach((q) => {
        if (q[2] <= 0.05) return;
        any = true;
        umax = Math.max(umax, Math.abs(q[0]));
        gvmin = Math.min(gvmin, q[1]);
        gvmax = Math.max(gvmax, q[1]);
      });
      if (any && (small || s1 > 0.001 || zoomP > 0.999)) {
        // tighter frame margins on small screens; guard always active there
        const mx = small ? 12 : 38, mTop = small ? 76 : 40, mBot = small ? 210 : 88;
        R = Math.min(R, (w / 2 - mx) / umax, (h - mTop - mBot) / Math.max(0.08, gvmax - gvmin));
        const lowY = cy - gvmin * R, highY = cy - gvmax * R;
        if (lowY > h - mBot) cy -= lowY - (h - mBot);
        else if (highY < mTop) cy += mTop - highY;
      }

      const proj = (v, a) => {
        const q = dirOf(v, a);
        return [cx + q[0] * R, cy - q[1] * R, q[2]];
      };

      ctx.clearRect(0, 0, w, h);

      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, 6.2832);
      ctx.fillStyle = "rgba(27,25,23,0.028)";
      ctx.fill();
      ctx.strokeStyle = "rgba(27,25,23,0.16)";
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.lineWidth = 1;
      const seg = (a, b, front) => {
        ctx.beginPath();
        ctx.moveTo(a[0], a[1]);
        ctx.lineTo(b[0], b[1]);
        ctx.strokeStyle = front ? "rgba(27,25,23,0.20)" : "rgba(27,25,23,0.055)";
        ctx.stroke();
      };
      for (let lat = -90 + step; lat < 90; lat += step) {
        let prev = proj(vec(lat, 0));
        for (let lon = 4; lon <= 360; lon += 4) {
          const cur = proj(vec(lat, lon));
          seg(prev, cur, prev[2] > 0 && cur[2] > 0);
          prev = cur;
        }
      }
      for (let lon = 0; lon < 360; lon += step) {
        let prev = proj(vec(-88, lon));
        for (let lat = -84; lat <= 88; lat += 4) {
          const cur = proj(vec(lat, lon));
          seg(prev, cur, prev[2] > 0 && cur[2] > 0);
          prev = cur;
        }
      }

      const driftA = inv(zoomP, 0.22, 0.7);
      if (driftA > 0.01) {
        for (let i = 0; i < drift.length; i++) {
          const d = drift[i];
          d.t += d.v * dt * 6;
          if (d.t > 1.04) d.t -= 1.08;
          const b = slerp(clamp(d.t, 0, 1));
          const co = Math.cos(d.off), so = Math.sin(d.off);
          const q = proj([b[0] * co + nx * so, b[1] * co + ny * so, b[2] * co + nz * so], 0.004);
          if (q[2] <= 0.06) continue;
          const edge = Math.sin(Math.PI * clamp(d.t, 0, 1));
          const da = 0.38 * driftA * d.a * edge * clamp(q[2] * 2.4, 0, 1);
          ctx.beginPath();
          ctx.arc(q[0], q[1], d.r * 2.4, 0, 6.2832);
          ctx.fillStyle = "rgba(27,25,23," + (da * 0.16).toFixed(3) + ")";
          ctx.fill();
          ctx.beginPath();
          ctx.arc(q[0], q[1], d.r, 0, 6.2832);
          ctx.fillStyle = "rgba(27,25,23," + da.toFixed(3) + ")";
          ctx.fill();
        }
      }

      const navy = RETURN_COLOR;
      const pathPt = (a, b, nr, t, latOff, altK) => {
        const base = gcMix(a, b, t);
        const off = latOff * Math.sin(Math.PI * t);
        const co = Math.cos(off), so = Math.sin(off);
        return proj([base[0] * co + nr[0] * so, base[1] * co + nr[1] * so, base[2] * co + nr[2] * so], alt * altK * Math.sin(Math.PI * t));
      };
      const nTT = nrmOf(v0, v1), nOT = nrmOf(vOsa, v1);
      const LANES = [
        { a: v0, b: v1, nr: nTT, color: accent, lat: 0.052, altK: 1, rev: false, phase: 0, speed: 0.075, wide: 1.4, al: 0.72 },
        { a: v0, b: v1, nr: nTT, color: navy, lat: -0.058, altK: 0.7, rev: true, phase: 0.42, speed: 0.062, wide: 1.4, al: 0.72 },
        { a: vOsa, b: v1, nr: nOT, color: accent, lat: 0.028, altK: 0.82, rev: false, phase: 0.66, speed: 0.058, wide: 1, al: 0.46 },
        { a: vOsa, b: v1, nr: nOT, color: navy, lat: -0.032, altK: 0.58, rev: true, phase: 0.2, speed: 0.05, wide: 1, al: 0.46 },
      ];
      const drawMarker = (m, color, sc) => {
        ctx.beginPath();
        ctx.arc(m[0], m[1], 5.2 * sc, 0, 6.2832);
        ctx.fillStyle = "#F7F2E8";
        ctx.fill();
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(m[0], m[1], 2.1 * sc, 0, 6.2832);
        ctx.fillStyle = color;
        ctx.fill();
      };
      const drawTrail = (at, mt, color, sc) => {
        ctx.save();
        ctx.lineCap = "round";
        ctx.strokeStyle = color;
        const STEPS = 22, TAIL = 0.08;
        for (let i = 0; i < STEPS; i++) {
          const f0 = i / STEPS, f1 = (i + 1) / STEPS;
          const a0 = at(Math.max(0, mt - (1 - f0) * TAIL));
          const a1 = at(Math.max(0, mt - (1 - f1) * TAIL));
          ctx.globalAlpha = 0.6 * Math.pow(f1, 2.1);
          ctx.lineWidth = (0.8 + 3.2 * Math.pow(f1, 2)) * sc;
          ctx.beginPath();
          ctx.moveTo(a0[0], a0[1]);
          ctx.lineTo(a1[0], a1[1]);
          ctx.stroke();
        }
        ctx.restore();
      };
      if (arcP > 0.001) {
        LANES.forEach((L) => {
          const sc = 1;
          const at = (t) => pathPt(L.a, L.b, L.nr, L.rev ? 1 - t : t, L.lat, L.altK);
          ctx.save();
          ctx.setLineDash([7, 6]);
          ctx.lineWidth = L.wide;
          ctx.lineCap = "butt";
          ctx.strokeStyle = L.color;
          ctx.globalAlpha = L.al * arcP;
          ctx.beginPath();
          const N = 160, end = Math.max(1, Math.round(N * arcP));
          for (let i = 0; i <= end; i++) {
            const q = at(i / N);
            i === 0 ? ctx.moveTo(q[0], q[1]) : ctx.lineTo(q[0], q[1]);
          }
          ctx.stroke();
          ctx.restore();
          const mt = arcP < 0.999 ? arcP : ((now / 1000) * L.speed + L.phase) % 1;
          drawTrail(at, mt, L.color, sc);
          drawMarker(at(mt), L.color, sc);
        });
      }

      // cities + labels
      const hubA = inv(zoomP, 0.3, 0.8);
      const showSat = SHOW_SATELLITES;
      const boxes = [], labelJobs = [];
      const boxAt = (txt, x, y, dx, hh) => {
        const tw = ctx.measureText(txt).width;
        const x0 = dx < 0 ? x - tw : dx === 0 ? x - tw / 2 : x;
        return { x0: x0, x1: x0 + tw, y0: y - hh, y1: y + hh };
      };
      const cost = (b) => {
        const mEdge = small ? 12 : 6;
        let c = 0;
        boxes.forEach((o) => {
          const iw = Math.min(b.x1, o.x1 + 3) - Math.max(b.x0, o.x0 - 3);
          const ih = Math.min(b.y1, o.y1 + 2) - Math.max(b.y0, o.y0 - 2);
          if (iw > 0 && ih > 0) c += iw * ih * 4;
        });
        if (b.x0 < mEdge) c += (mEdge - b.x0) * 80;
        if (b.x1 > w - mEdge) c += (b.x1 - (w - mEdge)) * 80;
        if (b.y0 < 4) c += (4 - b.y0) * 80;
        if (b.y1 > h - 4) c += (b.y1 - (h - 4)) * 80;
        return c;
      };
      const choose = (q, txt, hh, ox, oy, dirs, tail) => {
        let best = null;
        [1, 1.9, 3.1, 4.4].forEach((ring) =>
          dirs.forEach((d) => {
            const bx = q[0] + d[0] * ox * ring, by = q[1] + d[1] * oy * ring;
            const nb = boxAt(txt, bx, by, d[0], hh);
            const b = tail ? { x0: nb.x0, x1: nb.x1, y0: nb.y0, y1: nb.y1 + tail } : nb;
            const sc = cost(b) + (ring - 1) * 34;
            if (!best || sc < best.sc) best = { x: bx, y: by, dx: d[0], b: b, sc: sc, ring: ring };
          })
        );
        return best;
      };
      const DIRS8 = [[1, 1], [1, -1], [-1, 1], [-1, -1], [0, -1], [0, 1], [1, 0], [-1, 0]];

      CITY.forEach((c) => {
        const isHub = !!c.hub;
        const local = isHub ? 0 : c.li;
        if (!isHub && local >= perHub) return;
        const base = c.of === "jp" ? 0 : c.of === "osa" ? 0.2 : 0.34;
        const t0 = isHub ? 0 : base + local * 0.1;
        let a = isHub ? hubA : showSat ? ease(inv(satP, t0, t0 + 0.26)) : 0;
        if (a <= 0.01) return;
        const q = proj(isHub ? vec(c.lat, c.lon) : satVec(c));
        if (q[2] <= 0.02) return;
        a *= clamp(q[2] * 3, 0, 1);
        ctx.save();
        ctx.globalAlpha = a;
        if (!isHub) {
          const hv = HUBV[c.of], sv = satVec(c);
          ctx.globalAlpha = a * 0.26;
          ctx.strokeStyle = "rgba(27,25,23,0.6)";
          ctx.lineWidth = 0.6;
          ctx.beginPath();
          for (let i = 0; i <= 16; i++) {
            const t = i / 16;
            const pt = proj(gcMix(hv, sv, t), 0.012 * Math.sin(Math.PI * t));
            i === 0 ? ctx.moveTo(pt[0], pt[1]) : ctx.lineTo(pt[0], pt[1]);
          }
          ctx.stroke();
          const cyc = ((now / 1000) * 0.14 + c.order * 0.17) % 1;
          const ht = cyc < 0.5 ? cyc * 2 : 2 - cyc * 2;
          const hopAt = (u) => proj(gcMix(hv, sv, clamp(u, 0, 1)), 0.02 * Math.sin(Math.PI * clamp(u, 0, 1)) + 0.006);
          ctx.strokeStyle = accent;
          ctx.lineCap = "round";
          for (let i = 0; i < 6; i++) {
            const f0 = i / 6, f1 = (i + 1) / 6, dir = cyc < 0.5 ? -1 : 1;
            const b0 = hopAt(ht + dir * (1 - f0) * 0.09), b1 = hopAt(ht + dir * (1 - f1) * 0.09);
            ctx.globalAlpha = a * 0.5 * Math.pow(f1, 2);
            ctx.lineWidth = 0.5 + 1.2 * Math.pow(f1, 2);
            ctx.beginPath();
            ctx.moveTo(b0[0], b0[1]);
            ctx.lineTo(b1[0], b1[1]);
            ctx.stroke();
          }
          const hp2 = hopAt(ht);
          ctx.globalAlpha = a * 0.85;
          ctx.beginPath();
          ctx.arc(hp2[0], hp2[1], 1.7, 0, 6.2832);
          ctx.fillStyle = accent;
          ctx.fill();
          ctx.globalAlpha = a * 0.55;
          ctx.beginPath();
          ctx.arc(q[0], q[1], 2, 0, 6.2832);
          ctx.fillStyle = "rgba(27,25,23,0.9)";
          ctx.fill();
          ctx.globalAlpha = a * 0.6;
          ctx.font = '400 9.5px "IBM Plex Mono", monospace';
          ctx.fillStyle = "rgba(27,25,23,0.85)";
          ctx.textBaseline = "middle";
          const txt = c.n.toUpperCase();
          const dirs = c.side < 0 ? DIRS8.slice().reverse() : DIRS8;
          const pick = choose(q, txt, 6, 9, 11, dirs, 0);
          // on phones, drop a label rather than place it crowded
          if (pick.sc < (phone ? 60 : 110)) {
            boxes.push(pick.b);
            const al = a * 0.6, tick = [q[0], q[1]];
            labelJobs.push(() => {
              ctx.globalAlpha = al;
              if (pick.ring > 1.5) {
                ctx.strokeStyle = "rgba(27,25,23,0.45)";
                ctx.lineWidth = 0.5;
                const ex = pick.dx < 0 ? pick.b.x1 + 3 : pick.dx === 0 ? (pick.b.x0 + pick.b.x1) / 2 : pick.b.x0 - 3;
                ctx.beginPath();
                ctx.moveTo(tick[0], tick[1]);
                ctx.lineTo(ex, pick.y);
                ctx.stroke();
              }
              ctx.font = '400 9.5px "IBM Plex Mono", monospace';
              ctx.fillStyle = "rgba(27,25,23,0.85)";
              ctx.textBaseline = "middle";
              ctx.textAlign = pick.dx < 0 ? "right" : pick.dx === 0 ? "center" : "left";
              ctx.fillText(txt, pick.x, pick.y);
              ctx.textAlign = "left";
            });
          }
          ctx.restore();
          return;
        }
        ctx.beginPath();
        ctx.arc(q[0], q[1], 3.6, 0, 6.2832);
        ctx.fillStyle = accent;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(q[0], q[1], 9.5, 0, 6.2832);
        ctx.strokeStyle = accent;
        ctx.lineWidth = 0.8;
        ctx.globalAlpha = a * 0.2;
        ctx.stroke();
        ctx.globalAlpha = a;
        ctx.font = '500 19px "EB Garamond", Georgia, serif';
        const hc = small
          ? [[-1, -1], [0, -1], [-1, 1], [1, -1], [-1, 0], [1, 1], [0, 1], [1, 0]] // prefer left/up, allow right when near the left edge
          : c.hub === "osa" ? [[-1, 1], [0, 1], [-1, -1], [-1, 0], [0, -1]] : [[1, -1], [0, -1], [1, 1], [1, 0], [0, 1]];
        const hp = choose(q, c.n, 11, 21, 15, hc, 19);
        boxes.push(hp.b);
        const hal = a, jp = c.jp;
        labelJobs.push(() => {
          ctx.globalAlpha = hal;
          ctx.textBaseline = "middle";
          ctx.textAlign = hp.dx < 0 ? "right" : hp.dx === 0 ? "center" : "left";
          ctx.fillStyle = "#1B1917";
          ctx.font = '500 19px "EB Garamond", Georgia, serif';
          ctx.fillText(c.n, hp.x, hp.y);
          if (jp) {
            ctx.font = '400 11px "Zen Old Mincho", serif';
            ctx.fillStyle = "rgba(27,25,23,0.55)";
            ctx.fillText(jp, hp.x, hp.y + 17);
          }
          ctx.textAlign = "left";
        });
        ctx.restore();
      });

      // labels drawn last so nothing crosses the type
      ctx.save();
      labelJobs.forEach((j) => j());
      ctx.restore();

      if (hero) {
        hero.style.opacity = heroFade;
        hero.style.transform = "translateY(" + -26 * (1 - heroFade) + "px)";
      }
      if (cue) cue.style.opacity = heroFade;
      const win = (a, b) => inv(p, a, a + 0.05) * (1 - inv(p, b, b + 0.05));
      const wins = [win(0.17, 0.4), win(0.47, 0.64), win(0.68, 0.82), win(0.86, 1.1)];
      caps.forEach((el, i) => {
        if (!el) return;
        el.style.opacity = wins[i];
        el.style.transform = "translateY(" + 14 * (1 - wins[i]) + "px)";
      });
    };

    const loop = (now) => {
      try {
        frame(now);
      } catch (e) {
        console.error("[Kakehashi]", e);
        return;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <section ref={sectionRef} data-testid="hero-globe-section" className="relative h-[380vh] md:h-[480vh]" style={{ background: "#F7F2E8", color: "#1B1917" }}>
      <div style={{ position: "sticky", top: 0, height: "100vh", overflow: "hidden" }}>
        <canvas ref={canvasRef} data-testid="hero-globe-canvas" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }} />

        <div
          ref={heroRef}
          data-testid="hero-overlay"
          className="absolute inset-0 flex flex-col items-center justify-center gap-4 sm:gap-[26px] text-center px-5 sm:px-10 pointer-events-none"
        >
          <div className="font-mono uppercase" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10.5, letterSpacing: ".34em", color: "rgba(27,25,23,.55)" }}>
            Tokyo · Toronto
          </div>
          <h1
            data-testid="hero-headline"
            className="text-[clamp(30px,8.5vw,44px)] sm:text-[clamp(44px,7.2vw,104px)]"
            style={{ margin: 0, fontFamily: "'EB Garamond', Georgia, serif", lineHeight: 0.98, fontWeight: 400, letterSpacing: "-.015em", maxWidth: "15em" }}
          >
            Cross between Japan and Canada <em style={{ fontStyle: "italic", color: "#8E2118" }}>and arrive succeeding.</em>
          </h1>
          <p className="text-[13px] sm:text-[clamp(15px,1.5vw,20px)]" style={{ margin: 0, fontFamily: "'Zen Old Mincho', serif", letterSpacing: ".1em", color: "rgba(27,25,23,.7)" }}>
            日本とカナダを、確かな橋で結ぶ。
          </p>
          <p data-testid="hero-subtext" className="text-[14px] sm:text-[18px]" style={{ margin: "6px 0 0", maxWidth: "30em", lineHeight: 1.62, color: "rgba(27,25,23,.66)" }}>
            Connecting Japan and Canada through business, education, culture, and opportunity. We are not a translation agency and not a consultancy. We are a trusted bridge — present in the room until the outcome is real.
          </p>
        </div>

        <div
          data-testid="hero-step-captions"
          className="absolute left-4 right-4 bottom-5 h-[168px] sm:left-12 sm:right-auto sm:bottom-[34px] sm:w-[min(420px,40vw)] sm:h-[190px] pointer-events-none"
        >
          {CAPTIONS.map((c, i) => (
            <div
              key={c.eyebrow}
              ref={(el) => (capRefs.current[i] = el)}
              data-testid={`hero-caption-0${i + 1}`}
              style={{ position: "absolute", left: 0, bottom: 0, opacity: 0 }}
            >
              <div className="mb-2.5 sm:mb-[14px]" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: ".28em", textTransform: "uppercase", color: "#8E2118" }}>
                {c.eyebrow}
              </div>
              <div className="text-[22px] sm:text-[30px]" style={{ fontFamily: "'EB Garamond', Georgia, serif", lineHeight: 1.18, letterSpacing: "-.01em" }}>
                {c.en}
              </div>
              <div
                className={c.jp ? "text-[12px] sm:text-[13.5px]" : "text-[13px] sm:text-[15px]"}
                style={
                  c.jp
                    ? { fontFamily: "'Zen Old Mincho', serif", letterSpacing: ".08em", color: "rgba(27,25,23,.6)", marginTop: 10 }
                    : { lineHeight: 1.6, color: "rgba(27,25,23,.62)", marginTop: 10 }
                }
              >
                {c.sub}
              </div>
            </div>
          ))}
        </div>

        <div
          ref={cueRef}
          data-testid="hero-scroll-cue"
          className="hidden sm:flex"
          style={{
            position: "absolute", left: "50%", bottom: 36, transform: "translateX(-50%)",
            flexDirection: "column", alignItems: "center", gap: 10,
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 9.5, letterSpacing: ".3em",
            textTransform: "uppercase", color: "rgba(27,25,23,.5)",
          }}
        >
          <span>Scroll</span>
          <span style={{ display: "block", width: 1, height: 34, background: "linear-gradient(rgba(27,25,23,.45), rgba(27,25,23,0))", animation: "kh-cue 2.6s ease-in-out infinite" }} />
        </div>
      </div>
    </section>
  );
};
